// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/utils/PersianDatePicker.tsx
// 
// این فایل را در همان مسیر بالا کپی کنید (روی فایل قبلی بنویسید)
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../components/Layout";
import { ChevronRight, ChevronLeft, Calendar } from "lucide-react";
import jalaliMoment from "jalali-moment";

const MONTHS = ["حمل","ثور","جوزا","سرطان","اسد","سنبله","میزان","عقرب","قوس","جدی","دلو","حوت"];
const DAYS   = ["ش","ی","د","س","چ","پ","ج"];

// ماه‌های میلادی برای نمایش زیر ماه شمسی
const GREGORIAN_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export function todayGregorian(): string {
  return jalaliMoment().locale("en").format("YYYY-MM-DD");
}

export function gregorianToJalaliStr(dateStr: string): string {
  if (!dateStr) return "";
  return jalaliMoment(dateStr, "YYYY-MM-DD").locale("fa").format("jYYYY/jMM/jDD");
}

// تابع جدید: تبدیل به تاریخ میلادی فرمت‌شده
export function gregorianToReadableStr(dateStr: string): string {
  if (!dateStr) return "";
  return jalaliMoment(dateStr, "YYYY-MM-DD").locale("en").format("YYYY/MM/DD");
}

// تابع جدید: تبدیل به فرمت کوتاه میلادی (مثلاً: 2 May 2026)
export function gregorianToShortStr(dateStr: string): string {
  if (!dateStr) return "";
  const m = jalaliMoment(dateStr, "YYYY-MM-DD").locale("en");
  return `${m.format("D")} ${GREGORIAN_MONTHS[parseInt(m.format("M"))-1]} ${m.format("YYYY")}`;
}

// تابع جدید: نمایش هر دو تاریخ زیر هم برای استفاده در لیست‌ها
// خروجی: { jalali: "1405/02/12", gregorian: "2026/05/02" }
export function getDualDate(dateStr: string): { jalali: string; gregorian: string } {
  if (!dateStr) return { jalali: "", gregorian: "" };
  // مهم: میلادی را اول بگیر، چون locale("fa") روی همان object تأثیر می‌گذارد
  const gregorian = jalaliMoment(dateStr, "YYYY-MM-DD").locale("en").format("YYYY/MM/DD");
  const jalali    = jalaliMoment(dateStr, "YYYY-MM-DD").locale("fa").format("jYYYY/jMM/jDD");
  return { jalali, gregorian };
}

export function jalaliToGregorianStr(jy: number, jm: number, jd: number): string {
  return jalaliMoment(`${jy}/${jm}/${jd}`, "jYYYY/jMM/jDD").locale("en").format("YYYY-MM-DD");
}

// تابع جدید: نمایش هر دو تاریخ در یک HTML کوچک (برای پرینت و گزارش)
// خروجی: '<div style="line-height:1.3"><div>1405/02/12</div><div style="font-size:0.85em;color:#3b82f6">2 May 2026</div></div>'
export function gregorianToBothHtml(dateStr: string): string {
  if (!dateStr) return "";
  const dual = getDualDate(dateStr);
  const greg = gregorianToShortStr(dateStr);
  return `<div style="line-height:1.3;display:flex;flex-direction:column;gap:1px"><div style="font-weight:600">${dual.jalali}</div><div style="font-size:0.78em;color:#3b82f6;font-family:monospace">${greg}</div></div>`;
}

// نسخه ساده (متن دو خط با \n)
export function gregorianToBothStr(dateStr: string): string {
  if (!dateStr) return "";
  const dual = getDualDate(dateStr);
  const greg = gregorianToShortStr(dateStr);
  return `${dual.jalali}\n${greg}`;
}

export function getTodayJalali(): { jy: number; jm: number; jd: number } {
  const m = jalaliMoment().locale("en");
  return { jy: parseInt(m.format("jYYYY")), jm: parseInt(m.format("jMM")), jd: parseInt(m.format("jDD")) };
}

// ====================================================
// کامپوننت DualDate - نمایش هر دو تاریخ شمسی + میلادی
// در لیست‌ها و معاملات استفاده می‌شود
// ====================================================
interface DualDateProps {
  date: string;
  fontSize?: number;
  align?: "right" | "center" | "left";
  color?: string;       // رنگ شمسی
  gregColor?: string;   // رنگ میلادی (پیش‌فرض: آبی)
  inline?: boolean;     // اگر true، در یک خط نمایش می‌دهد
}

export function DualDate({ date, fontSize = 12, align = "right", color, gregColor = "#3b82f6", inline = false }: DualDateProps) {
  if (!date) return null;
  const dual = getDualDate(date);
  const greg = gregorianToShortStr(date);

  if (inline) {
    return (
      <span style={{ fontSize, color, display: "inline-flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontWeight: 600 }}>{dual.jalali}</span>
        <span style={{ fontSize: fontSize - 2, color: gregColor, fontWeight: 600, fontFamily: "monospace", opacity: 0.85 }}>
          {greg}
        </span>
      </span>
    );
  }

  return (
    <span style={{
      display: "inline-flex", flexDirection: "column", alignItems: align === "center" ? "center" : align === "left" ? "flex-start" : "flex-end",
      lineHeight: 1.2, gap: 1,
    }}>
      <span style={{ fontSize, color, fontWeight: 600 }}>{dual.jalali}</span>
      <span style={{ fontSize: fontSize - 2.5, color: gregColor, fontWeight: 600, fontFamily: "monospace", opacity: 0.9 }}>
        {greg}
      </span>
    </span>
  );
}

interface Props {
  value: string;
  onChange: (val: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  inputStyle?: React.CSSProperties;
}

export default function PersianDatePicker({ value, onChange, label, required, disabled, inputStyle: iStyle }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen]           = useState(false);
  const [mode, setMode]           = useState<"day"|"month"|"year">("day");

  const today = getTodayJalali();

  const parsed = value
    ? jalaliMoment(value, "YYYY-MM-DD")
    : jalaliMoment();

  const [viewY, setViewY] = useState(parseInt(parsed.format("jYYYY")));
  const [viewM, setViewM] = useState(parseInt(parsed.format("jMM")));

  const selJY = value ? parseInt(jalaliMoment(value,"YYYY-MM-DD").format("jYYYY")) : null;
  const selJM = value ? parseInt(jalaliMoment(value,"YYYY-MM-DD").format("jMM"))   : null;
  const selJD = value ? parseInt(jalaliMoment(value,"YYYY-MM-DD").format("jDD"))   : null;

  // نمایش مقدار: شمسی + میلادی
  const dual = value ? getDualDate(value) : { jalali: "", gregorian: "" };

  const dim  = jalaliMoment(`${viewY}/${viewM}/1`, "jYYYY/jMM/jDD").jDaysInMonth();
  const fdow = (jalaliMoment(`${viewY}/${viewM}/1`, "jYYYY/jMM/jDD").day() + 1) % 7;

  // محاسبه ماه و سال میلادی برای نمایش در هدر
  const firstOfMonth = jalaliMoment(`${viewY}/${viewM}/1`, "jYYYY/jMM/jDD");
  const lastOfMonth  = jalaliMoment(`${viewY}/${viewM}/${dim}`, "jYYYY/jMM/jDD");
  const firstGregM = parseInt(firstOfMonth.format("M"));
  const lastGregM  = parseInt(lastOfMonth.format("M"));
  const firstGregY = parseInt(firstOfMonth.format("YYYY"));
  const lastGregY  = parseInt(lastOfMonth.format("YYYY"));
  
  // اگر ماه شمسی روی دو ماه میلادی افتاده، هردو را نشان دهیم
  const gregMonthDisplay = firstGregM === lastGregM
    ? GREGORIAN_MONTHS[firstGregM-1]
    : `${GREGORIAN_MONTHS[firstGregM-1]}-${GREGORIAN_MONTHS[lastGregM-1]}`;
  const gregYearDisplay = firstGregY === lastGregY
    ? firstGregY
    : `${firstGregY}/${lastGregY}`;

  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setMode("day"); }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  // وقتی تقویم باز می‌شود، اگر value هست، روی ماه آن برو؛ اگر نه، روی ماه امروز
  useEffect(() => {
    if (open) {
      if (value) {
        const m = jalaliMoment(value, "YYYY-MM-DD");
        setViewY(parseInt(m.format("jYYYY")));
        setViewM(parseInt(m.format("jMM")));
      } else {
        setViewY(today.jy);
        setViewM(today.jm);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pick = (d: number) => {
    onChange(jalaliToGregorianStr(viewY, viewM, d));
    setOpen(false);
    setMode("day");
  };

  const base: React.CSSProperties = {
    width: "100%", padding: "9px 12px",
    background: dark ? "rgba(255,255,255,0.05)" : "#f8faff",
    border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    borderRadius: 8, color: T.text, fontSize: 13,
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
    direction: "rtl" as const, outline: "none",
    boxSizing: "border-box" as const, cursor: "pointer",
    opacity: disabled ? 0.5 : 1,
    minHeight: 42,  // برای جا دادن دو خط
  };

  const decadeStart = Math.floor(viewY / 10) * 10;
  const decadeYears = Array.from({ length: 12 }, (_, i) => decadeStart - 1 + i);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>
          {label}{required && <span style={{ color: "#ef4444", marginRight: 2 }}>*</span>}
        </label>
      )}

      {/* فیلد ورودی - با نمایش هر دو تاریخ */}
      <div
        onClick={() => !disabled && setOpen(!open)}
        style={{ ...base, ...iStyle, display: "flex", alignItems: "center", justifyContent: "space-between", userSelect: "none" }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
          {dual.jalali ? (
            <>
              <span style={{ color: T.text, fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>
                {dual.jalali}
              </span>
              <span style={{ color: "#3b82f6", fontSize: 10, fontWeight: 600, lineHeight: 1.2, fontFamily: "monospace" }}>
                {gregorianToShortStr(value)}
              </span>
            </>
          ) : (
            <span style={{ color: T.subtext, fontSize: 13 }}>انتخاب تاریخ...</span>
          )}
        </div>
        <Calendar size={14} color="#3b82f6" />
      </div>

      {/* تقویم باز شده */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 99999,
          background: dark ? "#1e293b" : "#ffffff",
          border: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
          borderRadius: 16, padding: 16, width: 320,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          direction: "rtl",
        }}>

          {/* ── هدر ناوبری با میلادی ── */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <button onClick={() => {
              if (mode === "day") { if (viewM === 1) { setViewM(12); setViewY(viewY-1); } else setViewM(viewM-1); }
              else if (mode === "year") setViewY(viewY - 10);
            }} style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", color: T.text, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ChevronRight size={14} />
            </button>

            <div style={{ display: "flex", gap: 6, flex: 1, justifyContent: "center" }}>
              {/* ماه - با میلادی زیرش */}
              <button onClick={() => setMode(mode === "month" ? "day" : "month")} style={{
                padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                background: mode === "month" ? "rgba(59,130,246,0.15)" : dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                color: mode === "month" ? "#3b82f6" : T.text, fontFamily: "inherit",
                display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{MONTHS[viewM-1]}</span>
                <span style={{ fontSize: 9.5, color: "#3b82f6", marginTop: 2, fontWeight: 700, fontFamily: "monospace" }}>{gregMonthDisplay}</span>
              </button>
              {/* سال - با میلادی زیرش */}
              <button onClick={() => setMode(mode === "year" ? "day" : "year")} style={{
                padding: "5px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                background: mode === "year" ? "rgba(59,130,246,0.15)" : dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                color: mode === "year" ? "#3b82f6" : T.text, fontFamily: "inherit",
                display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{viewY}</span>
                <span style={{ fontSize: 9.5, color: "#3b82f6", marginTop: 2, fontWeight: 700, fontFamily: "monospace" }}>{gregYearDisplay}</span>
              </button>
            </div>

            <button onClick={() => {
              if (mode === "day") { if (viewM === 12) { setViewM(1); setViewY(viewY+1); } else setViewM(viewM+1); }
              else if (mode === "year") setViewY(viewY + 10);
            }} style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)", color: T.text, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ChevronLeft size={14} />
            </button>
          </div>

          {/* ── انتخاب ماه ── */}
          {mode === "month" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
              {MONTHS.map((m, i) => (
                <button key={m} onClick={() => { setViewM(i+1); setMode("day"); }} style={{
                  padding: "8px 4px", border: "none", borderRadius: 8, cursor: "pointer",
                  fontSize: 12, fontFamily: "inherit",
                  background: viewM === i+1 ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                  color: viewM === i+1 ? "white" : T.text,
                  fontWeight: viewM === i+1 ? 700 : 400,
                }}>
                  {m}
                </button>
              ))}
            </div>
          )}

          {/* ── انتخاب سال ── */}
          {mode === "year" && (
            <div>
              <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                {[1360, 1370, 1380, 1390, 1400, 1410].map(y => (
                  <button key={y} onClick={() => { setViewY(y); }} style={{ padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 11, fontFamily: "inherit", background: Math.abs(viewY - y) < 10 ? "rgba(59,130,246,0.15)" : dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", color: Math.abs(viewY - y) < 10 ? "#3b82f6" : T.subtext, fontWeight: 600 }}>
                    {y}s
                  </button>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4 }}>
                {decadeYears.map(y => (
                  <button key={y} onClick={() => { setViewY(y); setMode("day"); }} style={{
                    padding: "7px 4px", border: "none", borderRadius: 8, cursor: "pointer",
                    fontSize: 12, fontFamily: "inherit",
                    background: viewY === y ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : selJY === y ? "rgba(59,130,246,0.15)" : dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                    color: viewY === y ? "white" : selJY === y ? "#3b82f6" : T.text,
                    fontWeight: viewY === y || selJY === y ? 700 : 400,
                  }}>
                    {y}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── انتخاب روز - با تاریخ میلادی زیر هر روز ── */}
          {mode === "day" && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 6 }}>
                {DAYS.map(d => (
                  <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 700, color: "#3b82f6", padding: "4px 0" }}>{d}</div>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
                {Array.from({ length: fdow }).map((_,i) => <div key={"e"+i} />)}
                {Array.from({ length: dim }).map((_,i) => {
                  const d = i+1;
                  const isSel  = selJY === viewY && selJM === viewM && selJD === d;
                  const isToday = today.jy === viewY && today.jm === viewM && today.jd === d;
                  
                  // محاسبه روز میلادی - با locale='en' برای دقت
                  const gregM = jalaliMoment(`${viewY}/${viewM}/${d}`, "jYYYY/jMM/jDD").locale("en");
                  const gregDay = parseInt(gregM.format("D"));
                  
                  return (
                    <button key={d} onClick={() => pick(d)} style={{
                      padding: "6px 0", border: "none", borderRadius: 8,
                      cursor: "pointer", fontFamily: "inherit",
                      background: isSel ? "linear-gradient(135deg,#3b82f6,#8b5cf6)" : isToday ? (dark?"rgba(59,130,246,0.18)":"rgba(59,130,246,0.1)") : "transparent",
                      color: isSel ? "white" : isToday ? "#3b82f6" : T.text,
                      display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1,
                      transition: "all 0.15s",
                      minHeight: 38,
                    }}
                      onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"; }}
                      onMouseLeave={e => { if (!isSel && !isToday) e.currentTarget.style.background = "transparent"; else if (isToday && !isSel) e.currentTarget.style.background = dark?"rgba(59,130,246,0.18)":"rgba(59,130,246,0.1)"; }}
                    >
                      <span style={{ fontSize: 13, fontWeight: isSel || isToday ? 800 : 600 }}>{d}</span>
                      <span style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        marginTop: 2,
                        color: isSel ? "rgba(255,255,255,0.9)" : "#3b82f6",
                        opacity: isSel ? 1 : 0.85,
                        fontFamily: "monospace",
                      }}>{gregDay}</span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => {
                  // اول viewY و viewM را به امروز ببر، سپس روی روز کلیک کن
                  const todayGreg = todayGregorian();
                  onChange(todayGreg);
                  setViewY(today.jy);
                  setViewM(today.jm);
                  setOpen(false);
                  setMode("day");
                }}
                style={{ marginTop: 12, width: "100%", padding: "8px", borderRadius: 8, border: "1.5px solid rgba(59,130,246,0.4)", background: "transparent", color: "#3b82f6", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", gap: 2, lineHeight: 1.2 }}
              >
                <span>📅 امروز — {MONTHS[today.jm-1]} {today.jd}، {today.jy}</span>
                <span style={{ fontSize: 10, opacity: 0.85, fontFamily: "monospace" }}>
                  {gregorianToShortStr(todayGregorian())}
                </span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}